/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Yup from "yup";
import {
  isFinanceUser,
  isDevUser,
  isSpecialUser,
  isSiteLeaseUser,
} from "../../utils";

export const MIN_DEV_FEE = 0.1845;
export const MIN_BESS_DEV_FEE = 40;
export type OptimizationType = "epc" | "discount" | "dev" | "site_lease";

const escalatorOptions = [
  { label: "0.00%", value: "0.0000" },
  { label: "0.49%", value: "0.0049" },
  { label: "0.75%", value: "0.0075" },
  { label: "0.99%", value: "0.0099" },
  { label: "1.00%", value: "0.0100" },
  { label: "1.25%", value: "0.0125" },
  { label: "1.49%", value: "0.0149" },
  { label: "1.50%", value: "0.0150" },
  { label: "1.99%", value: "0.0199" },
  { label: "2.00%", value: "0.0200" },
  { label: "2.25%", value: "0.0225" },
  { label: "2.49%", value: "0.0249" },
  { label: "2.50%", value: "0.0250" },
  { label: "2.75%", value: "0.0275" },
  { label: "2.90%", value: "0.0290" },
  { label: "2.99%", value: "0.0299" },
  { label: "3.00%", value: "0.0300" },
  { label: "3.50%", value: "0.0350" },
];

const termYearsOptions = [
  { label: "10", value: "10" },
  { label: "15", value: "15" },
  { label: "20", value: "20" },
  { label: "25", value: "25" },
];

const numberPattern = /^\d*\.?\d*$/;
const signedNumberPattern = /^-?\d*\.?\d*$/;

export const createValidationSchema = (
  values,
  opportunityState: string,
  opportunityUtility: string,
  systemDesignData: any,
) => {
  const schema = {};
  formFields.forEach((field) => {
    if (
      shouldRenderInput(
        field,
        values,
        opportunityState,
        opportunityUtility,
        systemDesignData,
      )
    ) {
      const isDisabled = false;
      if (isDisabled) {
        schema[field.name] = Yup.mixed().notRequired();
      } else {
        // Proceed with existing validation logic
        const requiredMessage = "Required.";
        switch (field.name) {
          case "pv_size_dc_kw_sd": {
            const pvSizeMin = isSpecialUser() ? 24 : 25;
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                pvSizeMin,
                `The entered system size is outside of the permitted range of (${pvSizeMin}kWdc - 15,000kWdc)`,
              )
              .max(
                15000,
                `The entered system size is outside of the permitted range of (${pvSizeMin}kWdc - 15,000kWdc)`,
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;
          }

          case "rooftop_pv_dc_kw":
            schema[field.name] = Yup.number()
              .min(
                0,
                "The entered system size is outside of the permitted range (0kWdc - 15,000kWdc)",
              )
              .max(
                15000,
                "The entered system size is outside of the permitted range (0kWdc - 15,000kWdc)",
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;
          case "carport_pv_dc_kw":
            schema[field.name] = Yup.number()
              .min(
                0,
                "The entered system size is outside of the permitted range of (0kWdc - 15,000kWdc)",
              )
              .max(
                15000,
                "The entered system size is outside of the permitted range of (0kWdc - 15,000kWdc)",
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;
          case "ground_mount_pv_dc_kw":
            schema[field.name] = Yup.number()
              .min(
                0,
                "The entered system size is outside of the permitted range of (0kWdc - 15,000kWdc)",
              )
              .max(
                15000,
                "The entered system size is outside of the permitted range of (0kWdc - 15,000kWdc)",
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;
          case "avoided_cost_per_kwh_qt":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                0,
                "The entered avoided cost is outside of the permitted range ($0.0000/kWh - $0.9999/kWh)",
              )
              .max(
                0.9999,
                "The entered avoided cost is outside of the permitted range ($0.0000/kWh - $0.9999/kWh)",
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;
          case "dollars_kwh_ppa":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                0.0001,
                "The entered PPA rate needs to be greater than $0.0000/kWh",
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;
          case "dollars_kwh_esa":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                0.0001,
                "The entered ESA rate needs to be greater than $0.0000/kWh",
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;
          case "decommission_bond_nom":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                0.01,
                "The entered decommissioning bond needs to greater than $0.00",
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;
          case "disc_to_standalone_storage_avoided_cost_inp":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                -99.99,
                "The entered discount is outside of the permitted range (-99.99% - 100.00%)",
              )
              .max(
                100.0,
                "The entered discount is outside of the permitted range (-99.99% - 100.00%)",
              )
              .test(
                "is-signed-decimal",
                "Invalid format",
                (value) =>
                  value === undefined ||
                  signedNumberPattern.test(value.toString()),
              );
            break;
          case "disc_to_pre_system_utility_bill_pcnt_inp":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                -50.0,
                "The entered discount is outside of the permitted range (-50.00% - 99.99%)",
              )
              .max(
                99.99,
                "The entered discount is outside of the permitted range (-50.00% - 99.99%)",
              )
              .test(
                "is-signed-decimal",
                "Invalid format",
                (value) =>
                  value === undefined ||
                  signedNumberPattern.test(value.toString()),
              );
            break;

          case "kwh_per_kw_qt":
          case "kwh_per_kw_sd":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                900,
                "The entered kW/kWh (productivity) is outside of the permitted range (900.00 - 2,300.00kW/kWh)",
              )
              .max(
                2300,
                "The entered kW/kWh (productivity) is outside of the permitted range (900.00 - 2,300.00kW/kWh)",
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;

          case "epc_total_per_watt_inp_qt":
          case "epc_total_per_watt_sd":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .moreThan(
                0,
                "The entered total EPC cost must be greater than $0.00/watt",
              );
            break;

          case "sr_finl_dev_fee_per_watt_inp":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                0,
                "The entered SR financing dev fee must be at least $0.00/watt",
              )
              .test(
                "is-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;

          case "state_incentive_yr1_nom_qt":
          case "state_incentive_yr1_nom_sd":
            schema[field.name] = Yup.number();
            break;

          case "custom_opp_maintain_cost_applies":
            schema[field.name] = Yup.string().required(requiredMessage);
            break;

          case "custom_opp_maintain_cost_yr1_per_kw":
          case "custom_opp_maintain_cost_yr6_per_kw":
            if (values?.custom_opp_maintain_cost_applies === "Yes") {
              schema[field.name] = Yup.number().required(requiredMessage);
            }
            break;

          case "custom_opp_maintain_cost_escal":
            if (values?.custom_opp_maintain_cost_applies === "Yes") {
              schema[field.name] = Yup.string()
                .required(requiredMessage)
                .oneOf(escalatorOptions.map((option) => option.value));
            }
            break;

          case "site_lease_included":
            schema[field.name] = Yup.string().required(requiredMessage);
            break;

          case "site_lease_annual_nom":
            schema[field.name] = Yup.number().min(
              0,
              "The Annual Site Lease ($/Yr) must not be negative",
            );
            break;

          case "site_lease_term_inp":
            schema[field.name] = Yup.number().min(
              0,
              "The Site Lease Term must not be negative",
            );
            break;

          case "site_lease_upfront_nom":
            schema[field.name] = Yup.number().min(
              0,
              "The Site Lease Upfront ($) must not be negative",
            );
            break;

          case "financing_type":
            schema[field.name] = Yup.string().required(
              "A product must be selected to execute a pricing quote",
            );
            break;

          case "ppa_term_yrs_num":
            schema[field.name] = Yup.number().required(requiredMessage);
            break;
          case "ppa_escal_orig_term":
            schema[field.name] = Yup.string().required(requiredMessage);
            break;

          case "esa_term_yrs_num":
            schema[field.name] = Yup.number().required(requiredMessage);
            break;
          case "esa_escal_orig_term":
            schema[field.name] = Yup.string().required(requiredMessage);
            break;

          case "lease_term_yrs_num":
          case "lease_escal_orig_term":
            if (values?.financing_type === "Lease") {
              schema[field.name] = Yup.string().required(requiredMessage);
            }
            break;

          case "itc_ineligible_nom_qt":
            schema[field.name] = Yup.number().test(
              "ineligible-cost-validation",
              "ITC Ineligible Costs need to be less than EPC Costs",
              function (value) {
                const epcCostsPerKW =
                  this.parent.epc_total_per_watt_sd ||
                  this.parent.epc_total_per_watt_inp_qt ||
                  this.parent.epc_total_per_watt_inp_sd ||
                  systemDesignData.epc_total_per_watt_sd__c ||
                  systemDesignData.epc_total_per_watt_sd_dep__c;
                const pvSystemSize = this.parent.pv_size_dc_kw_sd;
                const epcCosts = pvSystemSize * 1000 * epcCostsPerKW;
                if (field.name === "itc_ineligible_nom_qt") return true;
                return (
                  value === undefined ||
                  value === null ||
                  !!(epcCosts && value < epcCosts)
                );
              },
            );
            break;
          case "custom_useful_life_term":
            schema[field.name] = Yup.number().test(
              "min-above-term",
              "The Custom Useful Life must be 35 years or less and must not be lower than the financial product term years",
              function (value) {
                const financingType = this.parent.financing_type;
                const ppaTerm = this.parent.ppa_term_yrs_num;
                const leaseTerm = this.parent.lease_term_yrs_num;
                const esaTerm = this.parent.esa_term_yrs_num;

                if (value === undefined || value === null) return true;

                if (value > 35) return false;

                if (financingType === "PPA" && ppaTerm) {
                  return value >= parseInt(ppaTerm);
                } else if (financingType === "Lease" && leaseTerm) {
                  return value >= parseInt(leaseTerm);
                } else if (financingType === "ESA" && esaTerm) {
                  return value >= parseInt(esaTerm);
                }

                return true;
              },
            );
            break;

          case "itc_adder_domestic_qt":
          case "itc_adder_domestic_pv_qt":
          case "itc_adder_domestic_bess_qt":
          case "itc_adder_energy_com_qt":
          case "itc_adder_low_inc_qt":
          case "pv_itc_safeharbored":
            schema[field.name] = Yup.string().required(
              "All ITC adders have to be configured otherwise turn off ITC Adders by selecting ITC Adders as No",
            );
            break;
          case "offtaker_holds_rec_rights":
            schema[field.name] = Yup.string().required(requiredMessage);
            break;

          case "ppa_prod_guar_included":
          case "esa_prod_guar_included":
            schema[field.name] = Yup.string();
            break;

          case "rec_type":
          case "rec_group":
          case "public_school":
          case "public_entity":
            schema[field.name] = Yup.string().required(requiredMessage);
            break;

          case "ac_system_size_multiplier":
          case "ac_system_size_estimated":
            schema[field.name] = Yup.number();
            break;

          case "utility_name":
            schema[field.name] = Yup.string().required(
              "Please fill out the Utility Name in opportunity",
            );
            break;

          case "capacity_payment_term":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                1,
                "The entered Capacity Payment Term (years) is outside of the permitted range (1 - 35)",
              )
              .max(
                35,
                "The entered Capacity Payment Term (years) is outside of the permitted range (1 - 35)",
              );
            break;

          case "capacity_payment_rate":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                0.01,
                "The entered Capacity Payment Rate is outside of the permitted range (>0.01)",
              )
              .test(
                "is-signed-decimal",
                "Invalid format",
                (value) =>
                  value === undefined || numberPattern.test(value.toString()),
              );
            break;

          case "capacity_payment_escalator":
            schema[field.name] = Yup.number()
              .required(requiredMessage)
              .min(
                0.0,
                "The entered Capacity Payment Escalator is outside of the permitted range (0.00% - 99.99%)",
              )
              .max(
                99.99,
                "The entered Capacity Payment Escalator is outside of the permitted range (0.00% - 99.99%)",
              )
              .test(
                "is-signed-decimal",
                "Invalid format",
                (value) =>
                  value === undefined ||
                  signedNumberPattern.test(value.toString()),
              );
            break;

          default:
            if (field.isRequired !== false) {
              schema[field.name] = Yup.mixed().required(requiredMessage);
            }
        }
      }
    }
  });

  return Yup.object().shape(schema);
};

export const formFields = [
  {
    name: "rooftop_pv_dc_kw",
    label: "Rooftop Solar (kWdc)",
    type: "number",
    flowRestrictions: ["requires-system-design"],
    disabledOnSD: true,
    withoutDollarSign: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "carport_pv_dc_kw",
    label: "Carport Solar (kWdc)",
    type: "number",
    flowRestrictions: ["requires-system-design"],
    disabledOnSD: true,
    withoutDollarSign: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "ground_mount_pv_dc_kw",
    label: "Ground Mount Solar (kWdc)",
    type: "number",
    flowRestrictions: ["requires-system-design"],
    disabledOnSD: true,
    withoutDollarSign: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "pv_size_dc_kw_sd",
    label: "PV System Size (kWdc)",
    type: "number",
    flowRestrictions: ["requires-system-design"],
    disabledOnSD: true,
    withoutDollarSign: true,
    isRequired: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "ac_system_size_kw",
    label: "AC System Size (kW)",
    type: "number",
    flowRestrictions: ["requires-system-design"],
    disabledOnSD: true,
    withoutDollarSign: true,
    isRequired: false,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "avoided_cost_per_kwh_qt",
    label: "Avoided Cost Rate ($/kWh)",
    type: "number",
    decimals: 4,
    isRequired: true,
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "discount_input_type",
    label: "Discount / Rate Input Type",
    type: "select",
    origin_options: [
      { label: "Discount", value: "Discount" },
      { label: "PPA Rate", value: "Rate", projectType: ["Solar-only"] },
      { label: "ESA Rate", value: "Rate", projectType: ["Solar + Storage"] },
      {
        label: "Capacity Payment Rate",
        value: "Rate",
        projectType: ["Storage-only"],
      },
    ],
    defaultValue: "Discount",
    isRequired: true,
    exchangePoints: ["BTM", ""],
    fieldValues: {
      project_type: ["Solar + Storage", "Solar-only", "Storage-only"],
    },
  },
  {
    name: "disc_to_pre_system_utility_bill_pcnt_inp",
    label: "Annual Discount to Utility Bill (%) INPUT",
    type: "number",
    decimals: 4,
    isPercentage: true,
    isRequired: true,
    allowNegative: true,
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
    getDisabled: (values: any) => values?.discount_input_type === "Rate",
    getHelperNote: (values: any) =>
      values?.discount_input_type === "Rate"
        ? "Calculated from the entered rate."
        : undefined,
  },
  {
    name: "dollars_kwh_ppa",
    label: "PPA Rate ($/kWh)",
    type: "number",
    decimals: 4,
    isRequired: true,
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: ["Solar-only"], discount_input_type: ["Rate"] },
  },
  {
    name: "dollars_kwh_esa",
    label: "ESA Rate ($/kWh)",
    type: "number",
    decimals: 4,
    isRequired: true,
    exchangePoints: ["BTM", ""],
    fieldValues: {
      project_type: ["Solar + Storage"],
      discount_input_type: ["Rate"],
    },
  },
  {
    name: "kwh_per_kw_sd",
    label: "Productivity (kWh/kW)",
    type: "number",
    flowRestrictions: ["requires-system-design"],
    disabledOnSD: true,
    withoutDollarSign: true,
    decimals: 1,
    isRequired: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "epc_total_per_watt_sd",
    label: "EPC ($/W)",
    type: "number",
    decimals: 4,
    isRequired: true,
    getHelperNote: (values, systemDesignData) => {
      const epc_per_watt_sd_form_value = values.epc_total_per_watt_sd;
      const epc_per_watt_sd_form_salesforce =
        systemDesignData.epc_total_per_watt_sd_dep__c ||
        systemDesignData.epc_total_per_watt_sd__c;
      if (!epc_per_watt_sd_form_value) {
        return;
      }
      if (
        Math.abs(epc_per_watt_sd_form_value - epc_per_watt_sd_form_salesforce) >
        0.00005
      ) {
        return (
          "EPC per watt has been adjusted from $" +
          epc_per_watt_sd_form_salesforce.toFixed(4) +
          " to $" +
          epc_per_watt_sd_form_value.toString() +
          " and this quote cannot be saved." +
          " To save the quote with a different EPC cost, please update the value in System Design." +
          " If this change was not intended, please refresh the page to reset the value."
        );
      }
    },
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "epc_bess_per_kwh_sd",
    label: "EPC (BESS) ($/kWh)",
    type: "number",
    decimals: 4,
    isRequired: true,
    getHelperNote: (values, systemDesignData) => {
      const epc_per_watt_sd_form_value = values.epc_bess_per_kwh_sd;
      const bess_installer_cost_nom_sf =
        systemDesignData.bess_installer_cost_nom__c === ""
          ? 0
          : parseFloat(systemDesignData.bess_installer_cost_nom__c) || 0;
      const bess_sunrock_procurement_sf =
        systemDesignData.bess_sunrock_procurement__c === ""
          ? 0
          : parseFloat(systemDesignData.bess_sunrock_procurement__c) || 0;
      const bess_additional_epc_cost_nom_sf =
        systemDesignData.bess_additional_epc_cost_nom__c === ""
          ? 0
          : parseFloat(systemDesignData.bess_additional_epc_cost_nom__c) || 0;
      const bess_epc_total_sf =
        bess_installer_cost_nom_sf +
        bess_sunrock_procurement_sf +
        bess_additional_epc_cost_nom_sf;
      const bess_size_kwh_sf =
        systemDesignData.battery_energy_rating_kwh__c === ""
          ? 0
          : parseFloat(systemDesignData.battery_energy_rating_kwh__c) || 0;
      const epc_per_watt_sd_form_salesforce =
        bess_size_kwh_sf > 0 ? bess_epc_total_sf / bess_size_kwh_sf : undefined;
      if (!epc_per_watt_sd_form_value) {
        return;
      }
      if (!epc_per_watt_sd_form_salesforce) {
        return;
      }
      if (
        Math.abs(epc_per_watt_sd_form_value - epc_per_watt_sd_form_salesforce) >
        0.00005
      ) {
        return (
          "EPC (BESS) per watt has been adjusted from $" +
          epc_per_watt_sd_form_salesforce.toFixed(4) +
          " to $" +
          epc_per_watt_sd_form_value.toString() +
          " and this quote cannot be saved." +
          " To save the quote with a different EPC cost, please update the value in System Design." +
          " If this change was not intended, please refresh the page to reset the value."
        );
      }
    },
    fieldValues: { project_type: ["Storage-only"] },
  },
  {
    name: "itc_ineligible_nom_qt",
    label: "ITC Ineligible Cost (Quote)",
    type: "number",
    decimals: 2,
    isRequired: false,
  },
  {
    name: "dev_budget_override",
    label: "PD Budget Override",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: true,
  },
  {
    name: "dev_budget_exp_adjust_nom",
    label: "Development Budget Expense Adjustment ($)",
    type: "number",
    decimals: 2,
    isRequired: false,
    allowNegative: true,
    fieldValues: { dev_budget_override: ["No"] },
  },
  {
    name: "dev_only_expense_budget_total_nom_inp",
    label: "Development Expenses Only Budget Total Input ($)",
    type: "number",
    decimals: 2,
    isRequired: false,
    fieldValues: { dev_budget_override: ["Yes"] },
  },
  {
    name: "sr_finl_dev_fee_per_watt_inp",
    label: "Sunrock Financing Development Fee ($/W) INPUT",
    defaultValue: 0.369,
    type: "number",
    decimals: 4,
    // helperNote: `Note: Only Finance and Development can save quotes with a dev fee <${MIN_DEV_FEE}`,
    getHelperNote: (values) => {
      const devFee = Number(values.sr_finl_dev_fee_per_watt_inp);
      if (devFee < MIN_DEV_FEE) {
        return `Note: Only Finance and Development can save quotes with a dev fee < ${MIN_DEV_FEE}`;
      }
    },
    isRequired: false,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "sr_finl_dev_fee_per_kwh_inp",
    label: "Sunrock Financing Development Fee ($/kWh) INPUT",
    defaultValue: 90,
    type: "number",
    decimals: 4,
    // helperNote: `Note: Only Finance and Development can save quotes with a dev fee <${MIN_DEV_FEE}`,
    getHelperNote: (values) => {
      const devFee = Number(values.sr_finl_dev_fee_per_kwh_inp);
      if (devFee < MIN_BESS_DEV_FEE) {
        return `Note: Only Finance and Development can save quotes with a BESS dev fee < ${MIN_BESS_DEV_FEE}`;
      }
    },
    isRequired: false,
    fieldValues: { project_type: ["Storage-only", "Solar + Storage"] },
  },
  {
    name: "sr_finl_dev_fee_adj_appl",
    label: "Investor Dev Fee Adjustment Applies",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isHidden: isDevUser() || isFinanceUser() ? false : true,
    isRequired: false,
  },
  {
    name: "state_incentive_yr1_nom_qt",
    label: "State Incentive Upfront $",
    type: "number",
    decimals: 2,
    isRequired: false,
  },
  {
    name: "site_lease_included",
    label: "Site Lease",
    type: "select",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: false,
  },
  {
    name: "site_lease_upfront_nom",
    label: "Site Lease Upfront ($)",
    type: "number",
    decimals: 2,
    isRequired: false,
    fieldValues: { site_lease_included: ["Yes"] },
  },
  {
    name: "site_lease_annual_nom",
    label: "Annual Site Lease ($/Yr)",
    type: "number",
    decimals: 2,
    isRequired: false,
    fieldValues: { site_lease_included: ["Yes"] },
  },
  {
    name: "site_lease_term_inp",
    label: "Site Lease Term",
    type: "number",
    decimals: 0,
    withoutDollarSign: true,
    isRequired: false,
    fieldValues: { site_lease_included: ["Yes"] },
  },
  {
    name: "site_lease_escal",
    label: "Site Lease Escalator",
    type: "select",
    options: escalatorOptions,
    isRequired: true,
    fieldValues: { site_lease_included: ["Yes"] },
  },
  {
    name: "site_lease_solver_type",
    label: "Site Lease Solver Type",
    type: "select",
    origin_options: [
      { label: "Ongoing", value: "ongoing" },
      { label: "Upfront", value: "upfront" },
    ],
    defaultValue: "ongoing",
    isRequired: true,
    fieldValues: { site_lease_included: ["Yes"] },
  },
  {
    name: "unilateral_auto_renewal",
    label: "Unilateral Auto Renewal",
    type: "select",
    options: [
      { label: "Unilateral", value: "Unilateral" },
      { label: "Mutual", value: "Mutual" },
    ],
    defaultValue: "Unilateral",
    isRequired: false,
  },
  {
    name: "utility_name",
    label: "Utility Name",
    statesApplicable: ["IL"],
    type: "text",
    disabled: true,
    isRequired: true,
  },
  {
    name: "rec_type",
    label: "REC Type",
    type: "select",
    statesApplicable: ["NJ", "IL", "VA", "MI", "IN", "KY", "NC", "TN"],
    origin_options: [
      {
        label: "Adjustable Block Program",
        value: "Adjustable Block Program",
        statesApplicable: ["IL"],
      },
      {
        label: "Successor Solar Incentive (SuSI) Program",
        value: "Successor Solar Incentive (SuSI) Program",
        statesApplicable: ["NJ"],
      },
      { label: "VA REC", value: "VA REC", statesApplicable: ["VA"] },
      {
        label: "Pennsylvania Tier I RECs",
        value: "Pennsylvania Tier I RECs",
        statesApplicable: ["MI", "IN", "KY", "NC", "TN", "CA"],
      },
    ],
    fieldValues: { project_type: ["Solar-only", "Solar + Storage"] },
    isRequired: true,
  },
  {
    name: "rec_group",
    label: "REC Group",
    type: "select",
    statesApplicable: ["IL", "VA", "MI", "IN", "KY", "NC", "TN"],
    origin_options: [
      {
        label: "Group A: Ameren",
        value: "Group A: Ameren",
        statesApplicable: ["IL"],
      },
      {
        label: "Group B: ComEd",
        value: "Group B: ComEd",
        statesApplicable: ["IL"],
      },
      {
        label: "PJM",
        value: "PJM",
        statesApplicable: ["VA", "MI", "IN", "KY", "NC", "TN"],
      },
      {
        label: "Not Applicable",
        value: "Not Applicable",
        statesApplicable: ["IL", "VA", "MI", "IN", "KY", "NC", "TN"],
      },
    ],
    fieldValues: { project_type: ["Solar-only", "Solar + Storage"] },
    isRequired: true,
  },
  {
    name: "public_school",
    label: "Public School",
    type: "select",
    statesApplicable: ["IL", "CT"],
    defaultValue: "No",
    origin_options: [
      { label: "Yes", value: "Yes", statesApplicable: ["IL"] },
      { label: "No", value: "No", statesApplicable: ["IL"] },
      {
        label: "Yes",
        value: "Yes",
        statesApplicable: ["CT"],
        exchangePoints: ["FTM"],
      },
      {
        label: "No",
        value: "No",
        statesApplicable: ["CT"],
        exchangePoints: ["FTM"],
      },
    ],
    isRequired: true,
  },
  {
    name: "public_entity",
    label: "Public Entity",
    type: "select",
    statesApplicable: ["NJ"],
    origin_options: [
      { label: "Yes", value: "Yes", statesApplicable: ["NJ"] },
      { label: "No", value: "No", statesApplicable: ["NJ"] },
    ],
    isRequired: true,
  },
  {
    name: "financing_type",
    label: "Financing Type",
    type: "select",
    origin_options: [
      {
        label: "PPA",
        value: "PPA",
        exchangePoints: ["BTM", ""],
        projectType: ["Solar-only"],
      },
      {
        label: "ESA",
        value: "ESA",
        exchangePoints: ["BTM", ""],
        projectType: ["Storage-only", "Solar + Storage"],
      },
      {
        label: "Feed-in-Tariff",
        value: "Feed-in-Tariff",
        statesApplicable: ["MA"],
        projectType: ["Solar + Storage", "Solar-only"],
      },
      {
        label: "Feed-in-Tariff",
        value: "Feed-in-Tariff",
        exchangePoints: ["FTM"],
        statesApplicable: ["RI", "CT"],
        projectType: ["Solar + Storage", "Solar-only"],
      },
      {
        label: "Lease",
        value: "Lease",
        statesNotApplicable: ["MA", "RI", "CT"],
        projectType: ["Solar + Storage", "Solar-only"],
      },
    ],
    isRequired: true,
  },
  {
    name: "esa_term_yrs_num",
    label: "ESA Term",
    type: "select",
    options: termYearsOptions,
    defaultValue: "25",
    financingTypes: ["ESA", "Feed-in-Tariff"],
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: ["Solar + Storage", "Storage-only"] },
    isRequired: true,
  },
  {
    name: "esa_escal_orig_term",
    label: "ESA Escalator",
    type: "select",
    options: escalatorOptions,
    defaultValue: "0.0199",
    financingTypes: ["ESA", "Feed-in-Tariff"],
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: ["Solar + Storage", "Storage-only"] },
    isRequired: true,
  },
  {
    name: "ppa_term_yrs_num",
    label: "PPA Term",
    type: "select",
    options: termYearsOptions,
    defaultValue: "25",
    financingTypes: ["PPA", "Feed-in-Tariff"],
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: "Solar-only" },
    isRequired: true,
  },
  {
    name: "ppa_escal_orig_term",
    label: "PPA Escalator",
    type: "select",
    options: escalatorOptions,
    defaultValue: "0.0199",
    financingTypes: ["PPA", "Feed-in-Tariff"],
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: "Solar-only" },
    isRequired: true,
  },
  {
    name: "pv_incentive_program_type",
    label: "PV Incentive Program Type",
    type: "select",
    origin_options: [
      {
        label: "SMART",
        value: "SMART",
        statesApplicable: ["MA"],
        exchangePoints: ["FTM"],
      },
      {
        label: "REG",
        value: "REG",
        statesApplicable: ["RI"],
        exchangePoints: ["FTM"],
      },
      {
        label: "NRES",
        value: "NRES",
        statesApplicable: ["CT"],
        exchangePoints: ["FTM"],
      },
      {
        label: "SMART",
        value: "SMART",
        statesApplicable: ["MA"],
        exchangePoints: ["BTM"],
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "PPA",
        value: "PPA",
        exchangePoints: ["BTM"],
        fieldValues: { financing_type: "PPA" },
      },
    ],
    statesApplicable: ["MA", "RI", "CT"],
    isRequired: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "pv_incentive_program_group",
    label: "PV Incentive Program Group",
    type: "select",
    origin_options: [
      {
        label: "Total Comp",
        value: "Total Comp",
        statesApplicable: ["MA"],
        exchangePoints: ["FTM"],
      },
      {
        label: "Buy All",
        value: "Buy All",
        statesApplicable: ["CT"],
        exchangePoints: ["FTM"],
      },
      {
        label: "PPA",
        value: "PPA",
        exchangePoints: ["BTM"],
        fieldValues: { financing_type: "PPA" },
      },
      {
        label: "ESA",
        value: "ESA",
        exchangePoints: ["BTM"],
        fieldValues: { financing_type: "ESA" },
      },
      {
        label: "PPA",
        value: "PPA",
        exchangePoints: ["BTM"],
        statesApplicable: ["MA"],
        fieldValues: {
          financing_type: "Feed-in-Tariff",
          project_type: "Solar-only",
        },
      },
      {
        label: "ESA",
        value: "ESA",
        exchangePoints: ["BTM"],
        statesApplicable: ["MA"],
        fieldValues: {
          financing_type: "Feed-in-Tariff",
          project_type: "Solar + Storage",
        },
      },
    ],
    statesApplicable: ["MA", "RI", "CT"],
    isRequired: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "awarded_pv_incentive_program_total_per_kwh",
    label: "Awarded PV Incentive Program Total Rate ($/kWh)",
    type: "number",
    decimals: 4,
    exchangePoints: ["FTM"],
    statesApplicable: ["RI", "CT", "MA"],
    isRequired: false,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "community_type",
    label: "Community Type",
    type: "select",
    options: [
      { label: "Distressed Community", value: "Distressed Community" },
      { label: "None", value: "None" },
    ],
    statesApplicable: ["CT"],
    exchangePoints: ["FTM"],
    defaultValue: "None",
    isRequired: true,
  },
  {
    name: "rate_class",
    label: "Rate Class",
    type: "select",
    origin_options: [
      {
        label: "RD-1",
        value: "RD-1",
        utilityName: "UNITIL",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "RD-2",
        value: "RD-2",
        utilityName: "UNITIL",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "GD-1",
        value: "GD-1",
        utilityName: "UNITIL",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "GD-2",
        value: "GD-2",
        utilityName: "UNITIL",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "GD-3",
        value: "GD-3",
        utilityName: "UNITIL",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "GD-4",
        value: "GD-4",
        utilityName: "UNITIL",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-1",
        value: "R-1",
        utilityName: "Massachusetts Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-2",
        value: "R-2",
        utilityName: "Massachusetts Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-1",
        value: "G-1",
        utilityName: "Massachusetts Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-2 NEMA",
        value: "G-2 NEMA",
        utilityName: "Massachusetts Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-2 WMCA",
        value: "G-2 WMCA",
        utilityName: "Massachusetts Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-2 SEMA",
        value: "G-2 SEMA",
        utilityName: "Massachusetts Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-3 NEMA",
        value: "G-3 NEMA",
        utilityName: "Massachusetts Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-3 WCMA",
        value: "G-3 WCMA",
        utilityName: "Massachusetts Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-3 SEMA",
        value: "G-3 SEMA",
        utilityName: "Massachusetts Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-1",
        value: "R-1",
        utilityName: "Nantucket Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-2",
        value: "R-2",
        utilityName: "Nantucket Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-1",
        value: "G-1",
        utilityName: "Nantucket Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-2",
        value: "G-2",
        utilityName: "Nantucket Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-3",
        value: "G-3",
        utilityName: "Nantucket Electric",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-1/R-2 (All Eastern Massachusetts Territories)",
        value: "R-1/R-2 (All Eastern Massachusetts Territories)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-1HP/R-2HP (All Eastern Massachusetts Territories)",
        value: "R-1HP/R-2HP (All Eastern Massachusetts Territories)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-3/R-4 (All Eastern Massachusetts Territories)",
        value: "R-3/R-4 (All Eastern Massachusetts Territories)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-1ND (Greater Boston)",
        value: "G-1ND (Greater Boston)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-1D (Greater Boston)",
        value: "G-1D (Greater Boston)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-2 (NEMA)",
        value: "G-2 (NEMA)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-2 (SEMA)",
        value: "G-2 (SEMA)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-3 (NEMA)",
        value: "G-3 (NEMA)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-3 (SEMA)",
        value: "G-3 (SEMA)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "T-1",
        value: "T-1",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-1 (Cambridge)",
        value: "G-1 (Cambridge)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-2 (Cambridge)",
        value: "G-2 (Cambridge)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-3 (Cambridge)",
        value: "G-3 (Cambridge)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-5 (Cambridge)",
        value: "G-5 (Cambridge)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-6 (Cambridge)",
        value: "G-6 (Cambridge)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-1 (South Shore, Cape Cod, and Marthas)",
        value: "G-1 (South Shore, Cape Cod, and Marthas)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-2 (South Shore, Cape Cod, and Marthas)",
        value: "G-2 (South Shore, Cape Cod, and Marthas)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-3 (South Shore, Cape Cod, and Marthas)",
        value: "G-3 (South Shore, Cape Cod, and Marthas)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-4 (South Shore, Cape Cod, and Marthas)",
        value: "G-4 (South Shore, Cape Cod, and Marthas)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-6 (South Shore, Cape Cod, and Marthas)",
        value: "G-6 (South Shore, Cape Cod, and Marthas)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-7 (South Shore, Cape Cod, and Marthas)",
        value: "G-7 (South Shore, Cape Cod, and Marthas)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-1/R-2 (Western Massachusetts)",
        value: "R-1/R-2 (Western Massachusetts)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-1HP/R-2HP (Western Massachusetts)",
        value: "R-1HP/R-2HP (Western Massachusetts)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "R-3/R-4 (Western Massachusetts)",
        value: "R-3/R-4 (Western Massachusetts)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "23",
        value: "23",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "24",
        value: "24",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-1ND (Western Massachusetts)",
        value: "G-1ND (Western Massachusetts)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-1D (Western Massachusetts)",
        value: "G-1D (Western Massachusetts)",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-2",
        value: "G-2",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "T-4",
        value: "T-4",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "G-3",
        value: "G-3",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
      {
        label: "T-5",
        value: "T-5",
        utilityName: "Eversource",
        fieldValues: {
          financing_type: "Feed-in-Tariff",
        },
      },
    ],
    statesApplicable: ["MA"],
    isRequired: true,
  },
  {
    name: "greenfield_subtractor",
    label: "Greenfield Subtractor",
    type: "select",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    statesApplicable: ["MA"],
    isRequired: true,
  },
  {
    name: "acreage",
    label: "Acreage",
    type: "number",
    decimals: 2,
    statesApplicable: ["MA"],
    isRequired: true,
    withoutDollarSign: true,
  },
  {
    name: "property_tax_exempt",
    label: "Property Tax Exempt",
    type: "select",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    exchangePoints: ["FTM"],
    statesApplicable: ["MA"],
    isRequired: true,
  },
  {
    name: "lease_term_yrs_num",
    label: "Lease Term",
    type: "select",
    options: termYearsOptions,
    defaultValue: "25",
    isRequired: true,
    fieldValues: { financing_type: ["Lease"] },
  },
  {
    name: "lease_escal_orig_term",
    label: "Lease Escalator",
    type: "select",
    defaultValue: "0.0199",
    options: escalatorOptions,
    isRequired: true,
    fieldValues: { financing_type: ["Lease"] },
  },
  {
    name: "custom_useful_life_term",
    label: "Custom Useful Life Term",
    type: "number",
    decimals: 0,
    withoutDollarSign: true,
    isRequired: false,
  },
  {
    name: "itc_adder_domestic_qt",
    label: "Domestic ITC Adder Eligible",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: true,
  },
  {
    name: "itc_adder_domestic_pv_qt",
    label: "PV Domestic ITC Adder Eligible",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: true,
  },
  {
    name: "itc_adder_domestic_bess_qt",
    label: "BESS Domestic ITC Adder Eligible",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: true,
  },
  {
    name: "itc_adder_energy_com_qt",
    label: "Energy Community ITC Adder Eligible",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: true,
  },
  {
    name: "itc_adder_low_inc_qt",
    label: "Low Income ITC Adder Eligible",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: true,
  },
  {
    name: "pv_itc_safeharbored",
    label: "PV ITC Safe Harbored",
    type: "select",
    defaultValue: "Yes",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: true,
  },
  {
    name: "custom_opp_maintain_cost_applies",
    label: "Custom O&M",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "custom_opp_maintain_cost_yr1_per_kw",
    label: "Custom O&M Cost Year 1 ($/kW)",
    type: "number",
    decimals: 2,
    isRequired: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "custom_opp_maintain_cost_yr6_per_kw",
    label: "Custom O&M Cost Year 6 ($/kW)",
    type: "number",
    decimals: 2,
    isRequired: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "custom_opp_maintain_cost_escal",
    label: "Custom O&M Cost Escalator",
    type: "select",
    options: escalatorOptions,
    isRequired: true,
    fieldValues: { project_type: ["Solar + Storage", "Solar-only"] },
  },
  {
    name: "offtaker_holds_rec_rights",
    label: "Offtaker Holds REC Rights",
    type: "select",
    defaultValue: "No",
    origin_options: [
      {
        label: "Yes",
        value: "Yes",
        fieldValues: { financing_type: ["PPA", "Lease", "ESA"] },
      },
      {
        label: "No",
        value: "No",
        fieldValues: {
          financing_type: ["PPA", "Lease", "ESA", "Feed-in-Tariff"],
        },
      },
    ],
    fieldValues: { project_type: ["Solar-only", "Solar + Storage"] },
    isRequired: true,
  },
  {
    name: "decommission_bond_applies",
    label: "Decommission Bond Applies",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    isRequired: true,
  },
  {
    name: "decommission_bond_nom",
    label: "Decommissioning Bond Nominal Size ($)",
    type: "number",
    decimals: 2,
    isRequired: true,
    fieldValues: { decommission_bond_applies: ["Yes"] },
  },
  {
    name: "removal_fee_nom",
    label: "Removal Fee ($)",
    type: "number",
    decimals: 2,
    isRequired: true,
    exchangePoints: ["BTM"],
  },
  {
    name: "ppa_prod_guar_included",
    label: "PPA Production Guaranty Included",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    financingTypes: ["PPA"],
    isRequired: true,
  },
  {
    name: "esa_prod_guar_included",
    label: "ESA Production Guaranty Included",
    type: "select",
    defaultValue: "No",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    financingTypes: ["ESA"],
    isRequired: true,
  },
  {
    name: "battery_energy_rating_kwh",
    label: "Battery Energy Rating (kWh)",
    flowRestrictions: ["requires-system-design"],
    type: "number",
    disabledOnSD: true,
    withoutDollarSign: true,
  },
  {
    name: "battery_pwr_rating_kw",
    label: "Battery Power Rating (kW)",
    flowRestrictions: ["requires-system-design"],
    type: "number",
    disabledOnSD: true,
    withoutDollarSign: true,
  },
  {
    name: "extended_battery_life_product",
    label: "Extended Battery Life Product",
    type: "select",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    defaultValue: "Yes",
    isRequired: true,
  },
  {
    name: "es_oem",
    label: "Storage OEM",
    type: "select",
    options: [
      { label: "Tesla", value: "Tesla" },
      { label: "Socomec", value: "Socomec" },
      { label: "Other", value: "Other" },
    ],
    isRequired: true,
  },
  {
    name: "storage_warranty_default",
    label: "Default Storage Warranty Term?",
    type: "select",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    defaultValue: "Yes",
    isRequired: true,
  },
  {
    name: "storage_warranty_custom_term",
    label: "Custom Storage Warranty Term",
    type: "select",
    options: [
      { label: "5", value: "5" },
      { label: "10", value: "10" },
      { label: "15", value: "15" },
      { label: "20", value: "20" },
    ],
    isRequired: true,
  },
  {
    name: "storage_warranty_custom_cost",
    label: "Storage Warranty Custom Cost ($)",
    type: "number",
    decimals: 2,
    isRequired: true,
  },
  {
    name: "storage_incentive_program",
    label: "Storage Incentive Program(s)",
    type: "select",
    origin_options: [
      {
        label: "Demand Side Grid Support",
        value: "DSGS",
        statesApplicable: ["CA"],
        exchangePoints: ["BTM"],
      },
      {
        label: "Self-Generation Incentive Program",
        value: "SGIP",
        statesApplicable: ["CA"],
        exchangePoints: ["BTM"],
      },
      {
        label: "Clean Peak Energy Standard (CPEC)",
        value: "CPEC",
        statesApplicable: ["MA"],
        exchangePoints: ["BTM", "FTM"],
      },
      {
        label: "Commercial System Relief Program (CSRP)",
        value: "CSRP",
        statesApplicable: ["NY"],
        exchangePoints: ["BTM"],
      },
      {
        label: "MA Connected Solutions",
        value: "MACS",
        statesApplicable: ["MA"],
        exchangePoints: ["BTM"],
      },
      {
        label: "ISO-NE Forward Capacity Market (FCM)",
        value: "FCM",
        statesApplicable: ["MA"],
        exchangePoints: ["BTM", "FTM"],
      },
      {
        label: "CT Greenbank Energy Storage Solutions",
        value: "CTGESS",
        statesApplicable: ["CT"],
        exchangePoints: ["BTM"],
      },
      {
        label: "RI Connected Solutions",
        value: "RICS",
        statesApplicable: ["RI"],
        exchangePoints: ["BTM"],
      },
      {
        label: "NYSERDA Retail Storage Incentive",
        value: "NYSERDARSI",
        statesApplicable: ["NY"],
        exchangePoints: ["BTM", "FTM"],
      },
      {
        label: "Distribution Load Relief Program (DLRP)",
        value: "DLRP",
        statesApplicable: ["NY"],
        exchangePoints: ["BTM"],
      },
      {
        label: "Value of Distributed Energy Resources (VDER)",
        value: "VDER",
        statesApplicable: ["NY"],
        exchangePoints: ["BTM", "FTM"],
      },
    ],
    isRequired: false,
    multiple: true,
  },
  {
    name: "storage_additional_proj_diligence_req",
    label: "Storage Additional Project Diligence Required",
    type: "select",
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    defaultValue: "No",
    isRequired: false,
  },
  {
    name: "capacity_payment_included",
    label: "Capacity Payment (yes/no)",
    type: "select",
    origin_options: [
      { label: "Yes", value: "Yes" },
      {
        label: "No",
        value: "No",
        projectType: ["Solar + Storage"],
      },
    ],
    defaultValue: "Yes",
    isRequired: true,
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: ["Solar + Storage", "Storage-only"] },
  },
  {
    name: "standalone_storage_avoided_cost",
    label: "Annual Savings ($)",
    type: "number",
    decimals: 2,
    isRequired: true,
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: ["Storage-only"] },
  },
  {
    name: "disc_to_standalone_storage_avoided_cost_inp",
    label: "Annual Savings Discount (%)",
    type: "number",
    decimals: 4,
    isPercentage: true,
    isRequired: true,
    allowNegative: true,
    exchangePoints: ["BTM", ""],
    fieldValues: { project_type: ["Storage-only"] },
    getDisabled: (values: any) => values?.discount_input_type === "Rate",
    getHelperNote: (values: any) =>
      values?.discount_input_type === "Rate"
        ? "Calculated from the entered rate."
        : undefined,
  },
  {
    name: "capacity_payment_rate",
    label: "Capacity Payment Rate ($/kW-month)",
    type: "number",
    decimals: 4,
    isRequired: true,
    exchangePoints: ["BTM", ""],
  },
  {
    name: "capacity_payment_term",
    label: "Capacity Payment Term (years)",
    type: "select",
    options: termYearsOptions,
    defaultValue: 25,
    isRequired: true,
    exchangePoints: ["BTM", ""],
    fieldValues: {
      capacity_payment_included: ["Yes"],
      project_type: ["Storage-only"],
    },
  },
  {
    name: "capacity_payment_escalator",
    label: "Capacity Payment Escalator (%)",
    type: "select",
    options: escalatorOptions,
    defaultValue: "0.0199",
    isPercentage: true,
    isRequired: true,
    exchangePoints: ["BTM", ""],
    fieldValues: { capacity_payment_included: ["Yes"] },
  },
];

export const hasEpcComponentCosts = (systemDesignData: any): boolean => {
  const {
    installer_cost_nom__c,
    sr_equip_cost_nom__c,
    sr_dev_cost_nom__c,
    third_party_dev_cost_nom__c,
  } = systemDesignData;

  return [
    installer_cost_nom__c,
    sr_equip_cost_nom__c,
    sr_dev_cost_nom__c,
    third_party_dev_cost_nom__c,
  ].some((cost) => cost && cost > 0);
};

export const convertPercentToDecimal = (percent: number) => {
  return percent / 100;
};

const ITC_PV_BESS_FIELDS = [
  "itc_adder_domestic_qt",
  "itc_adder_domestic_pv_qt",
  "itc_adder_domestic_bess_qt",
  "pv_itc_safeharbored",
];

const itcHasPv = (projectType: string) => projectType !== "Storage-only";
const itcHasBess = (projectType: string) => projectType !== "Solar-only";

// Quotes saved before PV / BESS Domestic Content were split have neither field
// set; they keep using the legacy itc_adder_domestic_qt.
export const isPreChangeItcQuote = (values: any) =>
  !values?.itc_adder_domestic_pv_qt && !values?.itc_adder_domestic_bess_qt;

// Initial PV / BESS ITC values for the form. Pre-change quotes being edited or
// viewed stay on the legacy field; when cloned they are migrated so the legacy
// Domestic Content value applies to each technology in the project.
export const initialItcAdders = (
  prevFormData: any,
  projectType: string,
  action: string,
) => {
  const isExistingQuote = ["edit", "view", "clone"].includes(action);
  if (isExistingQuote && isPreChangeItcQuote(prevFormData)) {
    if (action !== "clone") {
      return {
        itc_adder_domestic_pv_qt: undefined,
        itc_adder_domestic_bess_qt: undefined,
        pv_itc_safeharbored: "No",
      };
    }
    const legacy =
      prevFormData?.itc_adder_domestic_qt === "Yes" ? "Yes" : "No";
    return {
      itc_adder_domestic_pv_qt: itcHasPv(projectType) ? legacy : "No",
      itc_adder_domestic_bess_qt: itcHasBess(projectType) ? legacy : "No",
      pv_itc_safeharbored: "No",
    };
  }
  return {
    itc_adder_domestic_pv_qt: prevFormData?.itc_adder_domestic_pv_qt || "No",
    itc_adder_domestic_bess_qt:
      prevFormData?.itc_adder_domestic_bess_qt || "No",
    pv_itc_safeharbored: prevFormData?.pv_itc_safeharbored || "Yes",
  };
};

// ITC adder values sent to the pricing model. Fields that don't apply to the
// project type are No, and the legacy itc_adder_domestic_qt is derived from the
// PV / BESS values. Pre-change quotes keep their legacy value, applied to each
// technology in the project, so they price the same as before.
export const resolveItcAdders = (values: any, projectType: string) => {
  const hasPv = itcHasPv(projectType);
  const hasBess = itcHasBess(projectType);
  if (isPreChangeItcQuote(values)) {
    const legacy = values?.itc_adder_domestic_qt === "Yes" ? "Yes" : "No";
    return {
      itc_adder_domestic_qt: legacy,
      itc_adder_domestic_pv_qt: hasPv ? legacy : "No",
      itc_adder_domestic_bess_qt: hasBess ? legacy : "No",
      pv_itc_safeharbored: "No",
    };
  }
  const pv = hasPv && values?.itc_adder_domestic_pv_qt === "Yes" ? "Yes" : "No";
  const bess =
    hasBess && values?.itc_adder_domestic_bess_qt === "Yes" ? "Yes" : "No";
  return {
    itc_adder_domestic_qt: pv === "Yes" || bess === "Yes" ? "Yes" : "No",
    itc_adder_domestic_pv_qt: pv,
    itc_adder_domestic_bess_qt: bess,
    pv_itc_safeharbored:
      hasPv && values?.pv_itc_safeharbored === "Yes" ? "Yes" : "No",
  };
};

// ITC adder values saved on the quote. Same as the model values, except
// pre-change quotes keep PV / BESS empty so they stay on the legacy field.
export const resolveItcAddersForQuote = (values: any, projectType: string) => {
  const resolved = resolveItcAdders(values, projectType);
  if (isPreChangeItcQuote(values)) {
    return {
      ...resolved,
      itc_adder_domestic_pv_qt: undefined,
      itc_adder_domestic_bess_qt: undefined,
    };
  }
  return resolved;
};

export const shouldRenderInput = (
  field: any,
  values: any,
  opportunityState: string,
  opportunityUtility: string,
  systemDesignData: any,
) => {
  if (field.flowRestrictions && field.flowRestrictions.length > 0) {
    for (const flowRestriction of field.flowRestrictions) {
      switch (flowRestriction) {
        case "requires-system-design":
          if (!systemDesignData) {
            return false;
          }
          break;
        default:
          throw new Error(`Unsupported flowRestriction: ${flowRestriction}`);
      }
    }
  }

  if (
    field.name.includes("custom_opp_maintain_cost") &&
    field.name !== "custom_opp_maintain_cost_applies"
  ) {
    return values?.custom_opp_maintain_cost_applies === "Yes";
  }

  if (field.name === "greenfield_subtractor") {
    const ground_mount_pv_dc_kw = parseFloat(
      systemDesignData?.ground_mount_pv_dc_kw__c,
    );
    if (
      isNaN(ground_mount_pv_dc_kw) ||
      ground_mount_pv_dc_kw <= 0 ||
      values?.financing_type === "PPA"
    )
      return false;
  }

  if (field.name === "acreage") {
    if (values?.greenfield_subtractor !== "Yes") return false;
  }

  if (field.name === "site_lease_term_inp") {
    if (!isSiteLeaseUser()) return false;
  }

  if (field.name === "unilateral_auto_renewal") {
    if (
      systemDesignData?.ix_delivery_point__c === "FTM" &&
      values?.site_lease_included !== "Yes"
    ) {
      return false;
    }
  }

  if (ITC_PV_BESS_FIELDS.includes(field.name)) {
    const projectType = values?.project_type || "Solar-only";
    const preChange = isPreChangeItcQuote(values);
    switch (field.name) {
      case "itc_adder_domestic_qt":
        // legacy field: only shown on quotes saved before PV / BESS split
        if (!preChange) return false;
        break;
      case "itc_adder_domestic_pv_qt":
      case "pv_itc_safeharbored":
        if (preChange || !itcHasPv(projectType)) return false;
        break;
      case "itc_adder_domestic_bess_qt":
        if (preChange || !itcHasBess(projectType)) return false;
        break;
    }
  }

  if (field.name === "pv_incentive_program_type") {
    if (
      values?.financing_type !== "Feed-in-Tariff" &&
      values?.financing_type !== "PPA"
    )
      return false;
  }

  if (field.statesApplicable && field.statesApplicable.length > 0) {
    if (
      !opportunityState ||
      !field.statesApplicable.includes(opportunityState)
    ) {
      return false;
    }
  }

  if (field.exchangePoints && field.exchangePoints.length > 0) {
    if (
      !field.exchangePoints.includes(systemDesignData?.ix_delivery_point__c)
    ) {
      return false;
    }
  }

  if (field.financingTypes && field.financingTypes.length > 0) {
    if (!field.financingTypes.includes(values?.financing_type)) {
      return false;
    }
  }

  if (field.fieldValues) {
    if (field.fieldValues) {
      for (const [key, value] of Object.entries(field.fieldValues)) {
        if (key === "field_name" || key === "field_values") continue;
        const currentFieldValue = values?.[key];
        if (Array.isArray(value)) {
          if (!value.includes(currentFieldValue)) {
            return false;
          }
        } else if (currentFieldValue !== value) {
          return false;
        }
      }
    }
  }

  const field_options = field.origin_options || field.options;
  if (field.type === "select" && field_options && field_options.length > 0) {
    const applicableOptions = field_options.filter(
      (option) =>
        (!option.statesApplicable ||
          option.statesApplicable.includes(opportunityState)) &&
        (!option.statesNotApplicable ||
          !option.statesNotApplicable.includes(opportunityState)) &&
        (!option.exchangePoints ||
          option.exchangePoints.includes(
            systemDesignData?.ix_delivery_point__c,
          )) &&
        (!option.projectType ||
          option.projectType.includes(systemDesignData?.project_type__c)) &&
        (!option.utilityName || option.utilityName === opportunityUtility) &&
        (!option.fieldValues ||
          Object.entries(option.fieldValues).every(([key, value]) => {
            if (key === "field_name" || key === "field_values") return true;
            const current = values?.[key];
            return Array.isArray(value)
              ? (value as unknown[]).includes(current)
              : current === value;
          })),
    );
    field.options = applicableOptions;
    if (applicableOptions.length === 0) {
      return false;
    }
  } else if (field.type === "select") return false;

  // battery fields
  if (
    [
      "battery_energy_rating_kwh",
      "battery_pwr_rating_kw",
      "extended_battery_life_product",
      "es_oem",
      "storage_warranty_default",
      "storage_warranty_cost",
      "storage_incentive_program",
      "storage_additional_proj_diligence_req",
    ].includes(field.name)
  ) {
    if (systemDesignData?.battery_storage_incl__c === "Yes") return true;
    return false;
  }

  if (
    ["storage_warranty_custom_cost", "storage_warranty_custom_term"].includes(
      field.name,
    )
  ) {
    if (values?.storage_warranty_default === "No") return true;
    return false;
  }

  if (field.name === "capacity_payment_rate") {
    const projectType =
      systemDesignData?.project_type__c || values?.project_type;
    if (projectType === "Storage-only") {
      return (
        values?.capacity_payment_included === "Yes" &&
        values?.discount_input_type === "Rate"
      );
    }
    return (
      projectType === "Solar + Storage" &&
      values?.capacity_payment_included === "Yes"
    );
  }

  return true;
};

export const stringToEscalator = (stringValue: string | undefined) => {
  const decimalPlaces = 4;
  if (!stringValue) {
    return "";
  }
  const parsedValue = parseFloat(stringValue);
  if (isNaN(parsedValue)) {
    return "";
  }
  return parsedValue.toFixed(decimalPlaces);
};

export const decimalPctgToString = (decimalPctg: number | undefined) => {
  if (!decimalPctg) {
    return 0;
  }
  const parsedValue = isNaN(decimalPctg) ? 0 : decimalPctg;
  if (parsedValue === 0) {
    return 0;
  }

  if (parsedValue >= 1) {
    return parsedValue;
  }
  return parsedValue * 100;
};
